/**
 * The acceptance audit.
 *
 * Read-only, because an acceptance is evidence and mutable evidence is not
 * evidence of anything. The rules say so — `create, update, delete: if false`
 * on `legalAcceptances` — and this screen only reads.
 *
 * **The uid belongs in the export here, and nowhere else.** The back office's
 * exporter refuses to write a uid into a file at all, because a spreadsheet of
 * students leaves the building in a way a web page does not. This file is the
 * opposite case: the record answers "who agreed to what, when, from where", and
 * without the identifier it answers nothing. The rest of the rule still holds —
 * no student appears in it, because no student ever accepts anything.
 */

export interface Acceptance {
  id: string;
  institutionId: string;
  uid: string;
  docId: string;
  version: number;
  acceptedAt: string;
  ip: string;
}

export function matchesQuery(
  acceptance: Acceptance,
  query: string,
  institutionName: (id: string) => string
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;

  return [
    acceptance.docId,
    acceptance.institutionId,
    acceptance.uid,
    institutionName(acceptance.institutionId),
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

/** Newest first: a DPO request is nearly always about something recent. */
export function sortAcceptances(rows: readonly Acceptance[]): Acceptance[] {
  return [...rows].sort((a, b) => b.acceptedAt.localeCompare(a.acceptedAt));
}

/**
 * One CSV cell.
 *
 * Quoted whenever it contains a separator, a quote or a newline, and a leading
 * `=`, `+`, `-` or `@` is prefixed with an apostrophe — a spreadsheet treats
 * those as formulas, and an export is opened by whoever asked for it rather
 * than by whoever produced it.
 */
export function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r;]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export const CSV_COLUMNS = [
  "institution",
  "institutionId",
  "uid",
  "docId",
  "version",
  "acceptedAt",
  "ip",
] as const;

/**
 * Exactly what is on the screen, in the order the columns appear.
 *
 * A byte-order mark so Excel opens a German export without mangling umlauts,
 * written as an escape because an invisible byte in a source file is a thing
 * nobody can review.
 */
export function toCsv(
  rows: readonly Acceptance[],
  institutionName: (id: string) => string,
  header: (column: (typeof CSV_COLUMNS)[number]) => string
): string {
  const head = CSV_COLUMNS.map((column) => csvCell(header(column))).join(",");
  const body = rows.map((row) =>
    [
      institutionName(row.institutionId),
      row.institutionId,
      row.uid,
      row.docId,
      row.version,
      row.acceptedAt,
      row.ip,
    ]
      .map(csvCell)
      .join(",")
  );
  return `\uFEFF${[head, ...body].join("\r\n")}\r\n`;
}

/** Named for the request it answers, not `export.csv` in a folder of them. */
export function exportFilename(query: string, today: string): string {
  const slug = query
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return ["acceptances", slug, today.slice(0, 10)].filter(Boolean).join("-") + ".csv";
}

/** Hands the file to the browser without anything reaching a server. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
